import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { ProfileInput } from "../../api/PsychologistApi";
import { TOPIC_KEYS } from "../../constants/funnel";

// Gedeelde bouwstenen voor het providerprofiel: de onboarding-wizard en de
// dashboard-tabs tonen exact dezelfde secties/velden (BasicsSection e.d.).

// "unsure" is an intake-only choice, never a psychologist specialization.
export const SPECIALIZATION_KEYS = TOPIC_KEYS.filter((k) => k !== "unsure");

export const PROFESSION_KEYS = ["klinisch_psycholoog", "klinisch_orthopedagoog", "psychotherapeut"] as const;
export const BUSINESS_FORM_KEYS = ["natural_person", "company"] as const;
export const VAT_STATUS_KEYS = ["none", "exempt_only", "franchise", "mixed"] as const;
export const WUG_PROFESSIONS = ["klinisch_psycholoog", "klinisch_orthopedagoog"];
export const ASSOCIATION_KEYS = ["vvkp", "vvgt", "vvpt", "bvrgs", "vvdo", "nvagt", "bwp_abp", "eap_ecp", "other"] as const;
export const LEGAL_BASIS_KEYS = ["grandfathered", "supervised"] as const;
export const DOC_KIND_KEYS = ["diploma", "erkenning", "visum", "association", "signature", "other"] as const;

export interface SectionProps {
  profile: ProfileInput;
  onChange: (p: ProfileInput) => void;
}

export function emptyProfile(): ProfileInput {
  return {
    displayName: "", bio: "", languages: [], regionsServed: [], specializations: [], gender: "",
    rateCents: 3750, coachingRateCents: 3750, therapyRateCents: 7500, offersCoaching: true, coachingTherapeuticPurpose: undefined, lateCancellationPolicy: "flexible",
    profession: "", visaNumber: "", erkenningNumber: "", rizivNumber: "", psychologenCommissieNumber: "",
    professionalAssociation: "", associationMemberNumber: "", psychotherapyLegalBasis: "",
    businessForm: "", vatStatus: "", vatNumber: "", tin: "", kboNumber: "", birthDate: "",
    addressStreet: "", addressPostalCode: "", addressCity: "",
  };
}

/** Map a /psychologists/me response onto the flat form state. */
export function profileFromApi(p: any): ProfileInput {
  return {
    displayName: p.displayName || "", bio: p.bio || "",
    languages: p.languages || [], regionsServed: p.regionsServed || [], specializations: p.specializations || [],
    gender: p.gender || "", rateCents: p.rateCents || 3750,
    coachingRateCents: p.coachingRateCents ?? p.rateCents ?? 3750,
    therapyRateCents: p.therapyRateCents ?? p.rateCents ?? 7500,
    offersCoaching: p.offersCoaching !== false,
    coachingTherapeuticPurpose: typeof p.coachingTherapeuticPurpose === "boolean" ? p.coachingTherapeuticPurpose : undefined,
    lateCancellationPolicy: p.lateCancellationPolicy || "flexible",
    profession: p.profession || "", visaNumber: p.visaNumber || "", erkenningNumber: p.erkenningNumber || "",
    rizivNumber: p.rizivNumber || "", psychologenCommissieNumber: p.psychologenCommissieNumber || "",
    professionalAssociation: p.professionalAssociation || "", associationMemberNumber: p.associationMemberNumber || "",
    psychotherapyLegalBasis: p.psychotherapyLegalBasis || "",
    businessForm: p.businessForm || "", vatStatus: p.vatStatus || "", vatNumber: p.vatNumber || "",
    tin: p.tin || "", kboNumber: p.kboNumber || "", birthDate: p.birthDate ? String(p.birthDate).slice(0, 10) : "",
    addressStreet: p.addressStreet || "", addressPostalCode: p.addressPostalCode || "", addressCity: p.addressCity || "",
  };
}

// Read a File as raw base64 (drop the data:...;base64, prefix — the API also strips it).
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^;]+;base64,/, ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Minimum om ter verificatie ingediend te kunnen worden — spiegelt
 *  speakhere-api/src/util/credentials.ts (validateCredentials). */
export function credentialsComplete(p: ProfileInput): boolean {
  if (!p.profession) return false;
  if (WUG_PROFESSIONS.includes(p.profession)) return !!p.visaNumber?.trim();
  if (p.profession === "psychotherapeut") return !!p.professionalAssociation && !!p.psychotherapyLegalBasis;
  return true;
}

/** Volledige DAC7/BTW-set — spiegelt speakhere-api/src/services/dac7.ts
 *  (missingDac7Fields) + de BTW-nummerregel uit util/credentials.ts. */
export function billingComplete(p: ProfileInput): boolean {
  if (!p.businessForm || !p.vatStatus) return false;
  if ((p.vatStatus === "franchise" || p.vatStatus === "mixed") && !p.vatNumber?.trim()) return false;
  // Hoofdadres: wettelijk verplicht in het DAC7-verkopersrapport.
  if (!p.addressStreet?.trim() || !p.addressPostalCode?.trim() || !p.addressCity?.trim()) return false;
  if (p.businessForm === "company") return !!p.kboNumber?.trim();
  return !!p.tin?.trim() && !!p.birthDate;
}

export function MultiChip({ label, options, selected, labelFor, onToggle }: {
  label: string; options: readonly string[]; selected: string[]; labelFor: (o: string) => string; onToggle: (o: string) => void;
}) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontSize: ".9rem", color: "text.secondary", mb: 1 }}>{label}</Typography>
      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
        {options.map((o) => (
          <Chip key={o} label={labelFor(o)} onClick={() => onToggle(o)}
            variant={selected.includes(o) ? "filled" : "outlined"} color={selected.includes(o) ? "primary" : "default"} />
        ))}
      </Stack>
    </Box>
  );
}

/** Toggle helper for the multi-select arrays (specializations/regions/languages). */
export function toggleIn(profile: ProfileInput, key: keyof ProfileInput, o: string): ProfileInput {
  const arr = (profile[key] as string[]) || [];
  return { ...profile, [key]: arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o] };
}
